import { useMemo, useState } from 'react';
import { VairUser } from 'constants/payloads/vair/playerSearch';
import { parseVairSportRating } from 'utils/shared/vair/parsePrimaryRating';
import NoPeople from 'svg/NoPeople';
import SearchIcon from 'svg/SearchIcon';
import WarnExclamation from 'svg/WarnExclamation';
import Button from 'components/Button';
import InfoAlert from 'components/InfoAlert';
import type { PartnerActionMode } from './EventGroupRegistrationCard';
import type { UserNeedingPartner } from './utils';

interface PartnerActionIdleProps {
  onRequestPartner: () => void;
  onAddPartner: () => void;
}

export function PartnerActionIdle({ onRequestPartner, onAddPartner }: PartnerActionIdleProps) {
  return (
    <>
      <Button type="button" variant="brand" size="md" onClick={onRequestPartner}>
        View players seeking a partner
      </Button>
      <Button type="button" variant="secondary" size="md" onClick={onAddPartner}>
        Invite a partner
      </Button>
      <InfoAlert
        title="No partner"
        desc="If you can't find a partner by the registration deadline, you will be refunded for the tournament."
        className="mt-4"
      />
    </>
  );
}

interface PartnerActionAddingByEmailProps {
  partnerEmail: string;
  onPartnerEmailChange: (value: string) => void;
  onSendRequest: () => void;
  onRequestLater: () => void;
  isLoading?: boolean;
}

export function PartnerActionAddingByEmail({
  partnerEmail,
  onPartnerEmailChange,
  onSendRequest,
  onRequestLater,
  isLoading,
}: PartnerActionAddingByEmailProps) {
  return (
    <>
      <input
        type="email"
        placeholder="Partner email"
        value={partnerEmail}
        onChange={(e) => onPartnerEmailChange(e.target.value)}
        className="input-form"
      />
      <span className="mb-4 mt-0.5 text-xs text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
        Add partner email address to be officially paired.
      </span>
      <div className="mt-ds-xl flex flex-col gap-ds-xl">
        <Button type="button" variant="brand" size="lg" onClick={onSendRequest} loading={isLoading}>
          Send request
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={onRequestLater}
          disabled={isLoading}
        >
          Request later
        </Button>
      </div>
    </>
  );
}

interface PartnerActionRequestingProps {
  usersNeedingPartner: UserNeedingPartner[];
  totalPartnerCount: number;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedUserId: string | null;
  selectedUser: UserNeedingPartner | undefined;
  onSelectUser: (userId: string | null) => void;
  onSendRequest: () => void;
  onRequestLater: () => void;
  isLoading?: boolean;
  vairUsersMap?: Record<string, VairUser>;
}

function getPlayerDisplayName(user: UserNeedingPartner): string {
  return user.userProfile?.fullName || user.userProfile?.preferredName || 'Unknown';
}

function PartnerActionRequestingWrapper({
  usersNeedingPartner,
  onRequestUser,
  onRequestLater,
  isLoading,
  vairUsersMap,
}: {
  usersNeedingPartner: UserNeedingPartner[];
  onRequestUser: (user: UserNeedingPartner) => void;
  onRequestLater: () => void;
  isLoading?: boolean;
  vairUsersMap?: Record<string, VairUser>;
}) {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return usersNeedingPartner;
    const q = searchQuery.trim().toLowerCase();
    return usersNeedingPartner.filter((u) => getPlayerDisplayName(u).toLowerCase().includes(q));
  }, [usersNeedingPartner, searchQuery]);
  const selectedUser = usersNeedingPartner.find((u) => u.userId === selectedUserId);

  return (
    <PartnerActionRequesting
      usersNeedingPartner={filteredUsers}
      totalPartnerCount={usersNeedingPartner.length}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      selectedUserId={selectedUserId}
      selectedUser={selectedUser}
      onSelectUser={setSelectedUserId}
      onSendRequest={() => selectedUser && onRequestUser(selectedUser)}
      onRequestLater={onRequestLater}
      isLoading={isLoading}
      vairUsersMap={vairUsersMap}
    />
  );
}

export function PartnerActionRequesting({
  usersNeedingPartner,
  totalPartnerCount,
  searchQuery,
  onSearchChange,
  selectedUserId,
  selectedUser,
  onSelectUser,
  onSendRequest,
  onRequestLater,
  isLoading,
  vairUsersMap,
}: PartnerActionRequestingProps) {
  const hasPartners = totalPartnerCount > 0;

  return (
    <div className="flex flex-col">
      <p className="typography-product-subheading mb-8">
        Players that need a partner ({totalPartnerCount}):
      </p>
      {!hasPartners ? (
        <div className="flex flex-col items-center justify-center gap-2">
          <NoPeople className="h-10 w-10 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary" />
          <p className="typography-product-caption max-w-[16rem] text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
            No players currently need a partner. You will be notified if that changes.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-color-border-input-lightmode bg-color-bg-lightmode-primary dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary">
          <div className="flex items-center gap-2 border-b border-color-border-input-lightmode bg-color-bg-input-lightmode-primary px-3 py-1 dark:border-color-border-input-darkmode dark:bg-color-bg-input-darkmode-primary">
            <SearchIcon className="h-5 w-5 shrink-0 text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder" />
            <input
              placeholder="Search player"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="input-form !border-0 pl-0 !outline-none !ring-0 !ring-offset-0"
            />
          </div>
          <ul className="max-h-[12rem] overflow-y-auto">
            {usersNeedingPartner.map((user) => {
              const isSelected = selectedUserId === user.userId;
              const parsed = parseVairSportRating(
                user.userId ? vairUsersMap?.[user.userId]?.sport?.pickleball : undefined,
              );
              const ratingDisplay = parsed.formattedValue;
              return (
                <li
                  key={user.userId}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectUser(isSelected ? null : user.userId);
                    }
                  }}
                  onClick={() => onSelectUser(isSelected ? null : user.userId)}
                  className="flex cursor-pointer items-center justify-between gap-4 px-3 py-3 transition-colors hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary"
                >
                  <span className="typography-product-body-highlight min-w-0 truncate">
                    {getPlayerDisplayName(user)}
                    {ratingDisplay && (
                      <span className="typography-product-body ml-1 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                        (VAIR {ratingDisplay})
                      </span>
                    )}
                  </span>
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                      isSelected
                        ? 'border-color-bg-lightmode-invert bg-color-bg-lightmode-invert'
                        : 'border-color-border-input-lightmode dark:border-color-border-input-darkmode'
                    }`}
                  >
                    {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className="mt-ds-xl flex flex-col gap-ds-xl">
        <Button
          type="button"
          variant="brand"
          size="lg"
          onClick={onSendRequest}
          disabled={!hasPartners || !selectedUser}
          loading={isLoading}
        >
          Send request
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={onRequestLater}
          disabled={isLoading}
        >
          Request later
        </Button>
      </div>
    </div>
  );
}

interface PartnerActionSectionProps {
  mode: PartnerActionMode;
  partnerEmail: string;
  usersNeedingPartner: UserNeedingPartner[];
  onModeChange: (mode: PartnerActionMode) => void;
  onPartnerEmailChange: (value: string) => void;
  onSendRequest: () => void;
  onRequestUser: (user: UserNeedingPartner) => void;
  onAddPartner: () => void;
  isLoading?: boolean;
  vairUsersMap?: Record<string, VairUser>;
}

export default function PartnerActionSection({
  mode,
  partnerEmail,
  usersNeedingPartner,
  onModeChange,
  onPartnerEmailChange,
  onSendRequest,
  onRequestUser,
  onAddPartner,
  isLoading,
  vairUsersMap,
}: PartnerActionSectionProps) {
  if (mode === 'adding-by-email') {
    return (
      <div className="mt-6 flex flex-col">
        <PartnerActionAddingByEmail
          partnerEmail={partnerEmail}
          onPartnerEmailChange={onPartnerEmailChange}
          onSendRequest={onSendRequest}
          onRequestLater={() => {
            onModeChange('idle');
            onPartnerEmailChange('');
          }}
          isLoading={isLoading}
        />
      </div>
    );
  }

  if (mode === 'requesting') {
    return (
      <div className="mt-6 flex flex-col gap-3">
        <PartnerActionRequestingWrapper
          usersNeedingPartner={usersNeedingPartner}
          onRequestUser={onRequestUser}
          onRequestLater={() => onModeChange('idle')}
          isLoading={isLoading}
          vairUsersMap={vairUsersMap}
        />
      </div>
    );
  }

  return (
    <div className="mt-6 flex flex-col gap-3">
      <PartnerActionIdle
        onRequestPartner={() => onModeChange('requesting')}
        onAddPartner={onAddPartner}
      />
    </div>
  );
}
